import { Box, BoxProps } from '@mui/material';
import Head from 'next/head';
import { DetailedHTMLProps, forwardRef, LinkHTMLAttributes, Ref } from 'react';

import LazyHydrate from '../LazyHydrate/LazyHydrate';

import { optimizeImageUrl, optimizeSrcSet, useIsHighPriorityImage } from '@src/utils/images/imageOptimization';

export function resolveEnv(): 'prod' | 'nonprod' {
	const hostName = globalThis?.location?.hostname;

	const isNonProd =
		!hostName || hostName.includes('localhost') || hostName.includes('stg.') || hostName.includes('stage');

	if (isNonProd) return 'nonprod';

	return 'prod';
}

export function incorrectUsageWarning(componentName: string, message: string) {
	const env = resolveEnv();

	if (env === 'prod') return null;

	console.warn(`⚠️⚠️⚠️ Shared Components Web - Incorrect use of ${componentName} component: ${message} ⚠️⚠️⚠️`);
}

type ImgProps = JSX.IntrinsicElements['img'];

interface IBasicImage extends ImgProps {
	alt: string;
	ref?: Ref<HTMLImageElement>;
	quality?: number | string;
}

export const BasicImage = forwardRef<HTMLImageElement, BoxProps & IBasicImage>(
	(props: BoxProps & IBasicImage, ref: Ref<HTMLImageElement> | null) => {
		const highPriority = useIsHighPriorityImage();

		const { src, alt, ...otherProps } = props;
		if (!src) {
			// if there is no src assuming that image is being handled by css
			return <Box ref={ref} {...otherProps} aria-label={alt} />;
		}
		const width = otherProps.width && `${+otherProps.width}` === otherProps.width ? otherProps.width : undefined;
		const optimizedSrc = optimizeImageUrl(src, width, otherProps.quality);
		// add source set if sizes is defined
		let srcSet: { srcSet: undefined | string } = { srcSet: undefined };
		if (optimizedSrc.includes('webp') && otherProps.sizes) {
			srcSet = { srcSet: optimizeSrcSet(src, otherProps.quality) };
		}

		let imageSize = props.sizes;
		imageSize ||= props.width ? `${props.width}` : undefined;

		return (
			<>
				{highPriority ? (
					<PreloadImage href={optimizedSrc} imageSrcSet={srcSet.srcSet} sizes={imageSize} />
				) : null}
				<Box
					ref={ref}
					component="img"
					loading={highPriority ? 'eager' : 'lazy'}
					{...srcSet}
					{...otherProps}
					src={optimizedSrc}
					alt={alt || ''}
				/>
			</>
		);
	}
);
// @ts-ignore
BasicImage.displayName = 'BasicImage';

type preloadLinkProps = DetailedHTMLProps<LinkHTMLAttributes<HTMLLinkElement>, HTMLLinkElement>;

export function PreloadImage(props: preloadLinkProps) {
	return (
		<LazyHydrate ssrOnly>
			<Head>
				<link rel="preload" as="image" {...props} />
			</Head>
		</LazyHydrate>
	);
}

interface IImageWrapper {
	/**
	 * Custom image element to be used instead of the default one. If you want to use the default image element, do not pass this prop.
	 *
	 * @type {typeof BasicImage}
	 * @memberof IImageWrapper
	 */
	imageEl?: React.ComponentType<any>;
	boxSx?: BoxProps['sx'];
	[imageProps: string]: unknown;
}

export default function ImageWrapper({ imageEl: ImageElement, ...imageProps }: Readonly<IImageWrapper>) {
	if (!ImageElement) {
		return <BasicImage {...imageProps} alt={imageProps.alt as string} />;
	}
	const { boxSx, ...rest } = imageProps;

	if (boxSx) {
		incorrectUsageWarning(
			'ImageWrapper',
			`boxSx prop should not be used with custom image element.
				 If you want to use Sx styling use the default rendering method of ImageWrapper. 
				 If you want to pass styles to the custom image element, pass them as "style" props.`
		);
	}

	return <ImageElement {...rest} alt={imageProps.alt as string} />;
}
